import { useMemo } from 'react';
import { ProjectId } from 'api/models';
import { ProjectPhase } from 'api/project';
import { Sprint } from 'api/sprint-planning/types';
import { StatusGroup, FieldValueType, FieldValue } from 'api/tasks';
import { Task } from 'data/tasks';
import { useProjectDetail } from './use-project-detail';
import { useSprints } from './use-sprints';

export type TaskCounterProp = {
  completed: number;
  todo: number;
  inProgress: number;
  blocked: number;
};

type ProjectPhaseStoreResponse = {
  projectPhase?: ProjectPhase;
  tasks: Task[];
  allTasks: Task[];
  completedTaskCount: number;
  inprogressTaskCount: number;
  firstLevelTaskCounter: TaskCounterProp;
  subTaskCounter: TaskCounterProp;
};
export const getTaskCounter = (
  fields: FieldValue<FieldValueType>[] = [],
): TaskCounterProp => ({
  completed:
    fields.filter(
      (subTaskStatus) =>
        subTaskStatus.metaFieldValue?.group === StatusGroup.Completed,
    )?.length || 0,
  inProgress:
    fields.filter(
      (subTaskStatus) =>
        subTaskStatus.metaFieldValue?.group === StatusGroup.InProgress,
    )?.length || 0,
  todo:
    fields.filter(
      (subTaskStatus) =>
        subTaskStatus.metaFieldValue?.group === StatusGroup.Todo,
    )?.length || 0,
  blocked:
    fields.filter(
      (subTaskStatus) =>
        subTaskStatus.metaFieldValue?.group === StatusGroup.Blocked,
    )?.length || 0,
});

export const useProjectPhaseFromStore = (
  projectId: number,
  phaseId: number,
): ProjectPhase | undefined => {
  const { data } = useProjectDetail(ProjectId(projectId));

  return useMemo(
    () =>
      data
        ? data.projectPhases.find(
            ({ projectPhaseId }) => phaseId === projectPhaseId,
          )
        : undefined,
    [data, phaseId],
  );
};

export const useProjectPhaseStore = (
  matchingPhaseTasks: Task[],
): ProjectPhaseStoreResponse => {
  const firstLevelTasks = useMemo(
    () =>
      matchingPhaseTasks.filter((task: Task) => {
        return task.parentTaskObject?.taskId == null;
      }),
    [matchingPhaseTasks],
  );
  const firstLevelTasksFields = useMemo(
    () =>
      firstLevelTasks.reduce(
        (result, item) => result.concat(item.fields),
        [] as FieldValue<FieldValueType>[],
      ),
    [firstLevelTasks],
  );
  const subTasksFields = useMemo(
    () =>
      matchingPhaseTasks
        .filter((task: Task) => task.parentTaskObject?.taskId != null)
        .reduce(
          (result, item) => result.concat(item.fields),
          [] as FieldValue<FieldValueType>[],
        ),
    [matchingPhaseTasks],
  );
  const firstLevelTaskCounter = useMemo(
    () => getTaskCounter(firstLevelTasksFields),
    [firstLevelTasksFields],
  );
  const subTaskCounter = useMemo(
    () => getTaskCounter(subTasksFields),
    [subTasksFields],
  );

  return useMemo(
    () => ({
      tasks: firstLevelTasks,
      allTasks: matchingPhaseTasks,
      completedTaskCount:
        firstLevelTaskCounter.completed + subTaskCounter.completed,
      inprogressTaskCount:
        firstLevelTaskCounter.inProgress + subTaskCounter.inProgress,
      firstLevelTaskCounter,
      subTaskCounter,
    }),
    [
      firstLevelTaskCounter,
      firstLevelTasks,
      matchingPhaseTasks,
      subTaskCounter,
    ],
  );
};

export const useProjectPhaseByProjectId = (
  projectId: number,
): ProjectPhase[] => {
  const { data } = useProjectDetail(ProjectId(projectId));

  return useMemo(() => {
    return data ? data.projectPhases : [];
  }, [data]);
};

export const useSprintsByProjectId = (
  projectId: number,
  options?: {
    refetchOnMount: boolean;
  },
): Sprint[] | undefined => {
  const { data } = useSprints(ProjectId(projectId), options);

  return data;
};
